import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const path = window.location.pathname;
            // Only redirect if not already on login/root/sso pages to avoid infinite loops
            if (path !== '/login' && path !== '/' && path !== '/sso') {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authApi = {
    login: async (username: string, password: string) => {
        const { data } = await api.post('/auth/login', { username, password });
        return data;
    },

    logout: async () => {
        await api.post('/auth/logout');
    },

    checkSession: async () => {
        const { data } = await api.get('/auth/session');
        return data;
    },

    ssoLogin: async (token: string) => {
        const { data } = await api.post('/auth/sso', { token });
        return data;
    },
};

// Sites API
export interface ListParams {
    search?: string;
    status?: 'all' | 'active' | 'inactive';
    page?: number;
    pageSize?: number;
}

export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    page: number;
    pageSize: number;
}

export const sitesApi = {
    getAll: async (params?: ListParams) => {
        const { data } = await api.get('/sites', { params });
        return data as PaginatedResponse<Record<string, unknown>>;
    },

    getById: async (id: number) => {
        const { data } = await api.get(`/sites/${id}`);
        return data;
    },

    create: async (site: {
        site_name: string;
        url: string;
        username: string;
        auth_token: string;
        description?: string;
    }) => {
        const { data } = await api.post('/sites', site);
        return data;
    },

    update: async (
        id: number,
        updates: Partial<{
            site_name: string;
            url: string;
            username: string;
            auth_token: string;
            description: string;
            is_active: boolean;
        }>
    ) => {
        const { data } = await api.put(`/sites/${id}`, updates);
        return data;
    },

    delete: async (id: number) => {
        const { data } = await api.delete(`/sites/${id}`);
        return data;
    },

    test: async (id: number) => {
        const { data } = await api.post(`/sites/${id}/test`);
        return data;
    },
};

// API Keys API
export const apiKeysApi = {
    list: async (params?: ListParams) => {
        const { data } = await api.get('/keys', { params });
        return data as PaginatedResponse<Record<string, unknown>>;
    },

    create: async (description: string) => {
        // Returns { id, key, description } — key is visible in this response only
        const { data } = await api.post('/keys', { description });
        return data as { id: number; key: string; description: string };
    },

    revoke: async (id: number) => {
        const { data } = await api.delete(`/keys/${id}`);
        return data;
    },

    toggle: async (id: number) => {
        const { data } = await api.patch(`/keys/${id}/toggle`);
        return data;
    },
};

// MCP API
export const mcpApi = {
    getStatus: async () => {
        const { data } = await api.get('/mcp/status');
        return data;
    },

    getTools: async () => {
        const { data } = await api.get('/mcp/tools');
        return data;
    },

    getLogs: async (limit: number = 50) => {
        const { data } = await api.get(`/mcp/logs?limit=${limit}`);
        return data;
    },
};

export const mediaApi = {
    upload: async (params: {
        file: File;
        site_name: string;
        alt_text?: string;
        title?: string;
    }): Promise<{ id: number; url: string; title: string; alt_text: string; mime_type: string }> => {
        const formData = new FormData();
        formData.append('file', params.file);
        formData.append('site_name', params.site_name);
        if (params.alt_text) formData.append('alt_text', params.alt_text);
        if (params.title)    formData.append('title', params.title);

        const { data } = await api.post('/media/upload', formData);
        return data;
    },
};

export default api;

